import { z } from "zod";
import { AgencyIdSchema, ProjectIdSchema, ReportIdSchema } from "../primitives/ids.js";
import { ExternalReadinessCategorySchema, ExternalReadinessPrioritySchema, ExternalReadinessSeveritySchema, } from "./external-readiness-report.js";
import { EXTERNAL_PROOF_FIX_KIT_RULE_SET_VERSION } from "./report-lifecycle.js";
export const ExternalProofFixKitIdSchema = z
    .string()
    .regex(/^fkit_ext_[a-zA-Z0-9_-]+$/)
    .brand();
export const ExternalProofFixKitLifecycleStatusSchema = z.enum([
    "FIX_KIT_DRAFT",
    "GENERATING",
    "READY_INTERNAL",
    "READY_CLIENT_SAFE_SUMMARY",
    "FAILED",
    "SUPERSEDED",
]);
export const ExternalProofFixKitEffortSchema = z.enum(["LOW", "MEDIUM", "HIGH"]);
export const ExternalProofFixKitActionItemV1Schema = z
    .object({
    schemaId: z.literal("externalProofFixKit.actionItem.v1"),
    actionItemId: z.string().min(1),
    sourceFindingId: z.string().min(1),
    sourceRef: z.string().min(1).max(2048),
    category: ExternalReadinessCategorySchema,
    severity: ExternalReadinessSeveritySchema,
    priority: ExternalReadinessPrioritySchema,
    effort: ExternalProofFixKitEffortSchema,
    issueSummary: z.string().min(1).max(500),
    whyItMatters: z.string().min(1).max(1000),
    whereIssueExists: z.string().min(1).max(500),
    recommendedFix: z.string().min(1).max(2000),
    suggestedCopy: z.string().min(1).max(2000).optional(),
    ctaRewriteSuggestion: z.string().min(1).max(1000).optional(),
    serviceClarityFix: z.string().min(1).max(1000).optional(),
    localServiceAreaFix: z.string().min(1).max(1000).optional(),
    schemaSearchReadinessSuggestion: z.string().min(1).max(1000).optional(),
    unsupportedClaimCleanup: z.string().min(1).max(1000).optional(),
    trustProofChecklist: z.array(z.string().min(1)).optional(),
    clientSafeExplanation: z.string().min(1).max(1000),
    agencyProposalLanguage: z.string().min(1).max(2000),
    optionalClientEmailDraft: z.string().min(1).max(2000).optional(),
    beforeAfterRelaunchAngle: z.string().min(1).max(1000),
    needsProof: z.boolean(),
})
    .strict();
/** Canonical internal storage document for Phase 5 external proof Fix Kit. */
export const ExternalProofFixKitV1Schema = z
    .object({
    schemaVersion: z.literal("external-proof-fix-kit.v1.0"),
    fixKitId: ExternalProofFixKitIdSchema,
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    sourceExternalReadinessReportId: ReportIdSchema,
    sourceFindingIds: z.array(z.string().min(1)),
    lifecycleStatus: ExternalProofFixKitLifecycleStatusSchema,
    ruleSetVersion: z.literal(EXTERNAL_PROOF_FIX_KIT_RULE_SET_VERSION),
    executiveSummary: z.string().min(1),
    actionItems: z.array(ExternalProofFixKitActionItemV1Schema),
    whatWeDoNotPromise: z.array(z.string().min(1)).min(1),
    agencyNotes: z.string().max(4000).optional(),
    beforeAfterRelaunchAngle: z.string().min(1),
    generatedAt: z.string().datetime(),
})
    .strict();
export const ExternalProofFixKitInternalViewV1Schema = z
    .object({
    schemaId: z.literal("externalProofFixKit.internalView.v1"),
    fixKitId: ExternalProofFixKitIdSchema,
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    sourceExternalReadinessReportId: ReportIdSchema,
    lifecycleStatus: ExternalProofFixKitLifecycleStatusSchema,
    executiveSummary: z.string().min(1),
    actionItems: z.array(ExternalProofFixKitActionItemV1Schema),
    whatWeDoNotPromise: z.array(z.string().min(1)).min(1),
    agencyNotes: z.string().max(4000).optional(),
    beforeAfterRelaunchAngle: z.string().min(1),
    generatedAt: z.string().datetime(),
    clientSafe: z.literal(false),
})
    .strict();
export const ExternalProofFixKitClientSafeActionItemV1Schema = z
    .object({
    issueSummary: z.string().min(1),
    severity: ExternalReadinessSeveritySchema,
    priority: ExternalReadinessPrioritySchema,
    recommendedFix: z.string().min(1),
    clientSafeExplanation: z.string().min(1),
})
    .strict();
export const ExternalProofFixKitClientSafeSummaryV1Schema = z
    .object({
    schemaId: z.literal("externalProofFixKit.clientSafeSummary.v1"),
    fixKitId: ExternalProofFixKitIdSchema,
    projectId: ProjectIdSchema,
    lifecycleStatus: z.literal("READY_CLIENT_SAFE_SUMMARY"),
    executiveSummary: z.string().min(1),
    actionItems: z.array(ExternalProofFixKitClientSafeActionItemV1Schema),
    whatWeDoNotPromise: z.array(z.string().min(1)).min(1),
    beforeAfterRelaunchAngle: z.string().min(1),
    generatedAt: z.string().datetime(),
    clientSafe: z.literal(true),
})
    .strict();
export const GenerateExternalProofFixKitRequestV1Schema = z
    .object({
    schemaId: z.literal("externalProofFixKit.generateRequest.v1"),
    forceRegenerate: z.boolean().optional(),
})
    .strict();
export const GenerateExternalProofFixKitResultV1Schema = z
    .object({
    schemaId: z.literal("externalProofFixKit.generateResult.v1"),
    fixKitId: ExternalProofFixKitIdSchema,
    lifecycleStatus: ExternalProofFixKitLifecycleStatusSchema,
    actionItemCount: z.number().int().min(0),
    sourceExternalReadinessReportId: ReportIdSchema,
    generatedAt: z.string().datetime(),
})
    .strict();
//# sourceMappingURL=external-proof-fix-kit.js.map